import { useNavigate } from 'react-router';
import { useMaps } from '../api/queries';
import type { Location } from '../api/types';
import { fmt, m2p } from '../lib/format';
import { useAuthedImage } from '../lib/hooks';
import { useT } from '../i18n';

/** Small map with the robot pose (robot control view). Click opens the map. */
export function PoseMiniMap({ mapName, location, className, width = 200, height = 165 }: { mapName: string | null | undefined; location: Location | null | undefined; className?: string; width?: number; height?: number }) {
  const t = useT();
  const nav = useNavigate();
  const maps = useMaps();
  const map = maps.data?.find((m) => m.name === mapName);
  const img = useAuthedImage(map?.hasImage ? `/api/maps/${map.id}/image?v=${encodeURIComponent(map.updatedAt)}` : null);
  if (!mapName) return null;
  // fit map into the box
  const scale = map ? Math.min(width / map.width, height / map.height) : 1;
  const ox = map ? (width - map.width * scale) / 2 : 0;
  const oy = map ? (height - map.height * scale) / 2 : 0;
  const pos = map && location ? m2p(map, location.x, location.y) : null;
  return (
    <div className={className} aria-label={t('robot.positionOnMap')} role="button" tabIndex={0} onClick={() => map && nav(`/maps/${map.id}`)} onKeyDown={(e) => e.key === 'Enter' && map && nav(`/maps/${map.id}`)}>
      {img.url && map && <img src={img.url} alt="" style={{ position: 'absolute', left: ox, top: oy, width: map.width * scale, height: map.height * scale, filter: 'invert(0.9)', opacity: 0.75 }} />}
      {pos && location && (
        <div style={{ position: 'absolute', left: ox + pos[0] * scale, top: oy + pos[1] * scale, width: 0, height: 0 }}>
          <svg width="24" height="24" viewBox="0 0 24 24" style={{ position: 'absolute', left: -12, top: -12, transform: `rotate(${90 - location.rotation}deg)` }}>
            <path d="M12 1 18 11H6z" fill="#5AA9E655" />
            <circle cx="12" cy="12" r="5" fill="#5AA9E6" stroke="#0D0F11" strokeWidth="2" />
          </svg>
        </div>
      )}
      <span style={{ position: 'absolute', left: 6, bottom: 6, fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-2)', background: 'rgba(13,15,17,.7)', padding: '1px 4px', borderRadius: 3 }}>
        {location ? `x ${fmt(location.x)} · y ${fmt(location.y)} · ${fmt(location.rotation, 0)}°` : mapName}
      </span>
    </div>
  );
}
